import { useMemo } from 'react'
import { useCalendarEvents } from '@/hooks/useCalendarEvents'
import { parseDateFromApi } from '@/lib/calendar-utils'
import type { AppointmentType } from '@/types/calendar'

interface GhostDayColumnProps {
	calendarType: AppointmentType
	date: string // YYYY-MM-DD
}

const START_HOUR = 7
const END_HOUR = 21
const TOTAL_HOURS = END_HOUR - START_HOUR
const HOUR_PX = 80

function timeToPixels(time: string): number {
	const [h, m] = time.split(':').map(Number)
	const minutes = (h - START_HOUR) * 60 + m
	return (minutes / 60) * HOUR_PX
}

const TOTAL_HEIGHT = TOTAL_HOURS * HOUR_PX

function formatDateDisplay(dateStr: string): string {
	const [year, month, day] = dateStr.split('-')
	return `${day}. ${month}. ${year}`
}

export function GhostDayColumn({ calendarType, date }: GhostDayColumnProps) {
	const dateObj = useMemo(() => (date ? parseDateFromApi(date) : new Date()), [date])

	const { events, isLoading } = useCalendarEvents({
		type: calendarType,
		from: dateObj,
		to: dateObj,
		enabled: !!date,
	})

	const hours = Array.from({ length: TOTAL_HOURS }, (_, i) => START_HOUR + i)

	return (
		// Outer div: no height — flexbox stretches it to match the form's height
		// without the timeline content expanding the row.
		<div className="w-64 flex-shrink-0 relative">
			{/* Absolute inner card fills the outer div exactly and scrolls internally */}
			<div className="absolute inset-0 flex flex-col bg-white rounded-2xl border border-[#e8e4df] shadow-sm p-3 overflow-hidden">
				{/* Column header */}
				<div className="mb-2 flex-shrink-0">
					<span className="text-base font-bold text-[#3d3428] uppercase tracking-wide">
						{calendarType}
					</span>
					<span className="ml-2 text-sm text-[#8c8275]">{formatDateDisplay(date)}</span>
				</div>

				{/* Scrollable timeline */}
				<div className="flex-1 overflow-y-auto min-h-0" style={{ scrollbarWidth: 'thin', scrollbarColor: '#d7ad6c #f5ebe0' }}>
					<div className="relative" style={{ height: TOTAL_HEIGHT }}>
						{/* Hour gridlines */}
						{hours.map((hour) => (
							<div
								key={hour}
								className="absolute left-0 right-0 border-t border-[#f0ece8]"
								style={{ top: (hour - START_HOUR) * HOUR_PX }}
							>
								<span className="text-[10px] text-[#c0b8b0] leading-none select-none">
									{String(hour).padStart(2, '0')}:00
								</span>
							</div>
						))}

						{/* Loading spinner */}
						{isLoading && (
							<div className="absolute inset-0 flex items-center justify-center">
								<div className="w-4 h-4 border-2 border-[#d7ad6c] border-t-transparent rounded-full animate-spin" />
							</div>
						)}

						{/* Empty state */}
						{!isLoading && events.length === 0 && (
							<div className="absolute inset-0 flex items-center justify-center">
								<span className="text-[11px] text-[#c0b8b0]">Žádné termíny</span>
							</div>
						)}

						{/* Appointment blocks */}
						{!isLoading &&
							events.map((event) => {
								const apt = event.resource
								const top = timeToPixels(apt.startTime)
								const bottom = timeToPixels(apt.endTime)
								const height = Math.max(bottom - top, 28)

								if (top >= TOTAL_HEIGHT || bottom <= 0) return null

								const bgColor = apt.background || '#d7ad6c'
								const clampedTop = Math.max(0, top)
								const clampedHeight = Math.min(height, TOTAL_HEIGHT - clampedTop)

								return (
									<div
										key={event.id}
										className="absolute left-7 right-1 rounded overflow-hidden"
										style={{
											top: clampedTop,
											height: clampedHeight,
											backgroundColor: bgColor,
											boxShadow: 'inset 0 1px 0 0 rgba(255,255,255,0.6), 0 1px 3px rgba(0,0,0,0.1)',
										}}
										title={`${apt.startTime}–${apt.endTime}  ${event.title}`}
									>
										<div className="px-1 py-0.5 h-full flex flex-col justify-start overflow-hidden">
											<div className="text-white font-semibold leading-tight whitespace-nowrap overflow-hidden text-ellipsis" style={{ fontSize: 'var(--font-size-event-title)' }}>
												{apt.startTime}–{apt.endTime}
											</div>
											<div className="text-white/90 leading-tight whitespace-nowrap overflow-hidden text-ellipsis" style={{ fontSize: 'var(--font-size-event-title)' }}>
												{event.title}
											</div>
										</div>
									</div>
								)
							})}
					</div>
				</div>
			</div>
		</div>
	)
}
